import React, { useState, useEffect, useCallback } from 'react';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../../components/NoData/NoData';
import StatusHighliter from '../../../components/StatusHighliter/StatusHighliter';
import {
  Form,
  Fieldset,
  Label,
  TextInput,
  MessageBox,
  ResetButton,
  Button,
  Dropdown,
} from '../../../components/FormElements';
import { TableBuilder } from '../../../components/TableElements';
import {
  StyledIconTitleContainer,
  StyledIconContainer,
  StyledTitleContainer,
  StyledSpan,
  StyledFiltersContainer,
  StyledRechargeCountsContainer,
  StyledRechargeCounter,
  StyledHeader,
} from '../../../components/Styled';
import { Image } from '../../../components/Image';
import Pagination from '../../../components/Pagination/Pagination';
import API from '../../../api';
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from '../../../constants';
import {
  getSearchParams,
  camelCaseToString,
  formatCurrency,
  shouldRedirect,
  redirectToPageOne,
} from '../../../utils';
import FiltersButton from '../../../components/FiltersButton/FiltersButton';
import { isEmpty } from 'lodash';
import moment from 'moment';

const MyFundRequestsTable = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 10,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [walletDepositRequests, setWalletDepositRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('All');
  const [processed, setProcessed] = useState('');
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [depositType, setDepositType] = useState('');
  const [upiTransactionId, setUpiTransactionId] = useState('');
  const [requestToOptions, setRequestToOptions] = useState([]);

  const readCounts = useCallback(
    (
      depositType = '',
      status = '',
      processed = '',
      startDate = '',
      endDate = '',
      upiTransactionId = ''
    ) => {
      setIsLoading(true);
      setResponseStatus('');
      setResponseMessage('');
      API.get(
        `/accounting/wallet-deposit-new/my-fund-requests-counts?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fUpiTransactionId=${upiTransactionId}&fDepositType=${depositType}&fStatus=${status}&fProcessed=${processed}&fStartDate=${startDate}&fEndDate=${endDate}`
      )
        .then((response) => {
          const { status, data } = response.data;
          if (status === API_RESPONSE_TYPES.SUCCESS) {
            setCounts(data[0]);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILURE);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    []
  );

  const clearFilters = () => {
    setStatus('All');
    setProcessed('');
    setDepositType('');
    setStartDate(moment().format('YYYY-MM-DD'));
    setEndDate(moment().format('YYYY-MM-DD'));
    setUpiTransactionId('');
  };

  const resetFilters = () => {
    clearFilters();
    readData(
      depositType,
      status,
      processed,
      startDate,
      endDate,
      upiTransactionId
    );
    readCounts(
      depositType,
      status,
      processed,
      startDate,
      endDate,
      upiTransactionId
    );
  };

  const readData = useCallback(
    (
      depositType = '',
      status = '',
      processed = '',
      startDate = '',
      endDate = '',
      upiTransactionId = ''
    ) => {
      setIsLoading(true);
      setResponseStatus('');
      setResponseMessage('');
      API.get(
        `/accounting/wallet-deposit-new/my-fund-requests?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fUpiTransactionId=${upiTransactionId}&fDepositType=${depositType}&fStatus=${status}&fProcessed=${processed}&fStartDate=${startDate}&fEndDate=${endDate}`
      )
        .then((response) => {
          const { status, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            if (status === API_RESPONSE_TYPES.SUCCESS) {
              setTotalRecords(pageInfo.totalRecords);
            }
            setWalletDepositRequests(data);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILURE);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData]
  );

  const reloadData = () => {
    readData(
      depositType,
      status,
      processed,
      startDate,
      endDate,
      upiTransactionId
    );
    readCounts(
      depositType,
      status,
      processed,
      startDate,
      endDate,
      upiTransactionId
    );
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  const onClickExport = () => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(
      `/accounting/wallet-deposit-new/my-fund-requests-export?fUpiTransactionId=${upiTransactionId}&fDepositType=${depositType}&fStatus=${status}&fProcessed=${processed}&fStartDate=${startDate}&fEndDate=${endDate}`
    )
      .then((response) => {
        const { status, message } = response.data;
        if (status === API_RESPONSE_TYPES.FAILURE) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setResponseStatus(status);
          setResponseMessage(message);
          setTimeout(() => {
            setResponseStatus('');
            setResponseMessage('');
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    if (!isFiltering) {
      readData(
        depositType,
        status,
        processed,
        startDate,
        endDate,
        upiTransactionId
      );
      readCounts(
        depositType,
        status,
        processed,
        startDate,
        endDate,
        upiTransactionId
      );
    }
  }, [paginationData, readData, readCounts]);

  useEffect(() => {
    readData(
      depositType,
      status,
      processed,
      startDate,
      endDate,
      upiTransactionId
    );
    readCounts(
      depositType,
      status,
      processed,
      startDate,
      endDate,
      upiTransactionId
    );
  }, []);

  const { accountsPermissions } = props;
  const canExport =
    accountsPermissions.includes(PERMISSION_TYPES.EXPORT) || false;

  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  const readWdMeta = useCallback(() => {
    API.get(`/configurations/wd-meta`).then((response) => {
      const { status, data } = response.data;
      if (status === API_RESPONSE_TYPES.SUCCESS) {
        setRequestToOptions(data?.requestToOptions);
      }
    });
  }, []);

  useEffect(() => {
    readWdMeta();
  }, []);

  const requestToOptionsList = [
    {
      title: '-- SELECT REQUEST TO ACCOUNT --',
      value: '',
    },
  ];
  if (requestToOptions.length > 0) {
    requestToOptions.forEach((item) => {
      requestToOptionsList.push({
        title: item.optionName,
        value: item.optionValue,
      });
    });
  }

  return (
    <>
      <PageHeading
        {...props}
        reloadData={reloadData}
        showExport={canExport}
        onClickExport={onClickExport}
      />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? 'show' : ''}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>Deposit Type</Label>
            <Dropdown
              style={{ border: '1px solid #cacaca' }}
              placeholder="Deposit Type"
              value={depositType}
              onChange={setDepositType}
              options={[
                {
                  title: '-- SELECT DEPOSIT TYPE --',
                  value: '',
                },
                {
                  title: 'Payment Gateway',
                  value: 'Payment Gateway',
                },
                {
                  title: 'Fund Requests',
                  value: 'Fund Requests',
                },
              ]}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>Status</Label>
            <Dropdown
              style={{ border: '1px solid #cacaca' }}
              placeholder="Status"
              value={status}
              onChange={setStatus}
              options={[
                {
                  title: '-- SELECT STATUS --',
                  value: '',
                },
                {
                  title: 'All',
                  value: 'All',
                },
                {
                  title: 'Pending',
                  value: 'Pending',
                },
                {
                  title: 'Approved',
                  value: 'Approved',
                },
                {
                  title: 'Rejected',
                  value: 'Rejected',
                },
              ]}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>Process</Label>
            <Dropdown
              style={{ border: '1px solid #cacaca' }}
              placeholder={'Requested To'}
              value={processed}
              onChange={setProcessed}
              options={requestToOptionsList}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>Start Date</Label>
            <TextInput
              type="date"
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              disabled={isLoading === true}
            />
          </Fieldset>
          <Fieldset>
            <Label>End Date</Label>
            <TextInput
              type="date"
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              disabled={isLoading === true}
            />
          </Fieldset>
          <Fieldset>
            <Label>UTR Number</Label>
            <TextInput
              value={upiTransactionId}
              onChange={setUpiTransactionId}
              placeholder="UTR Number"
              disabled={isLoading === true}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading === true} type="submit">
              Filter
            </Button>
            <ResetButton
              disabled={isLoading === true}
              type="button"
              onClick={resetFilters}
            >
              Clear
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <StyledRechargeCountsContainer>
        {counts.successAmount && (
          <StyledRechargeCounter color={'forestgreen'}>
            <StyledHeader>{`${formatCurrency(counts.successAmount)} ( ${counts.successCount
              } )`}</StyledHeader>
          </StyledRechargeCounter>
        )}
        {counts.failureAmount && (
          <StyledRechargeCounter color={'red'}>
            <StyledHeader>{`${formatCurrency(counts.failureAmount)} ( ${counts.failureCount
              } )`}</StyledHeader>
          </StyledRechargeCounter>
        )}
        {counts.pendingAmount && (
          <StyledRechargeCounter color={'#ff9800'}>
            <StyledHeader>{`${formatCurrency(counts.pendingAmount)} ( ${counts.pendingCount
              } )`}</StyledHeader>
          </StyledRechargeCounter>
        )}
      </StyledRechargeCountsContainer>
      {responseStatus && responseMessage && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      <LoadingSpinner
        isLoading={
          responseStatus === '' &&
          walletDepositRequests?.length === 0 &&
          isLoading
        }
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILURE &&
          !isLoading &&
          walletDepositRequests?.length === 0
        }
        message={`No fund requests found`}
      />
      {walletDepositRequests?.length > 0 && (
        <>
          <TableBuilder
            isLoading={walletDepositRequests?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: 'Requested To',
                dataSelector: 'toAccountsId',
                dataType: 'string',
                CellRenderer: (value, item) => {
                  const {
                    firstName,
                    lastName,
                    isEntity,
                    entityName,
                    roleName,
                    photo,
                    mobileNumber,
                  } = item;
                  return value === '' ? (
                    'N/A'
                  ) : value === '0' || value === 0 ? (
                    'Admin'
                  ) : (
                    <StyledIconTitleContainer>
                      <StyledIconContainer>
                        <Image
                          source={
                            photo && !isEmpty(photo) && photo !== 'null'
                              ? photo
                              : '/no-image.png'
                          }
                          alt={
                            isEntity === 'Yes'
                              ? entityName
                              : `${firstName} ${lastName}`
                          }
                          style={{ width: '50px', height: '50px' }}
                        />
                      </StyledIconContainer>
                      <StyledTitleContainer>
                        {isEntity === 'Yes'
                          ? entityName
                          : `${firstName} ${lastName}`}
                        <StyledSpan>{mobileNumber}</StyledSpan>
                        <StyledSpan>{camelCaseToString(roleName)}</StyledSpan>
                      </StyledTitleContainer>
                    </StyledIconTitleContainer>
                  );
                },
              },
              {
                title: 'Deposit Details',
                dataSelector: 'depositRequestType',
                dataType: 'string',
                CellRenderer: (value, item) => {
                  const { bankName, bankAccountNumberOrUpi } = item;
                  return (
                    <>
                      {value}
                      {bankName && <StyledSpan>{bankName}</StyledSpan>}
                      {bankAccountNumberOrUpi && (
                        <StyledSpan>{bankAccountNumberOrUpi}</StyledSpan>
                      )}
                    </>
                  );
                },
              },
              {
                title: 'UTR / Reference Number',
                dataSelector: 'transactionsUTRNumber',
                dataType: 'string',
                CellRenderer: (value) => (value ? value : 'N/A'),
              },
              {
                title: 'Amount',
                dataSelector: 'amount',
                dataType: 'number',
                align: 'center',
                CellRenderer: (value) => formatCurrency(value),
              },
              {
                title: 'Status',
                dataSelector: 'approvalStatus',
                dataType: 'string',
                align: 'center',
                CellRenderer: (value) =>
                  value && (
                    <StatusHighliter
                      className={
                        value === 'Approved'
                          ? 'green'
                          : value === 'Rejected'
                            ? 'red'
                            : 'yellow'
                      }
                    >
                      {value}
                    </StatusHighliter>
                  ),
              },
              {
                title: 'Transactions Date',
                dataSelector: 'transactionsDate',
                dataType: 'date',
                CellRenderer: (value) => (value ? value : 'N/A'),
              },
              {
                title: 'Requested On',
                dataSelector: 'createdOn',
                dataType: 'date',
                CellRenderer: (value) => (value ? value : 'N/A'),
              },
              {
                title: 'Processed On',
                dataSelector: 'approvalDate',
                dataType: 'date',
                CellRenderer: (value) => (value ? value : 'N/A'),
              },
              {
                title: 'My Remark',
                dataSelector: 'fromAccountsRemark',
                dataType: 'string',
                CellRenderer: (value) => (value ? value : 'N/A'),
              },
              {
                title: 'Approver Remark',
                dataSelector: 'toAccountsRemark',
                dataType: 'string',
                CellRenderer: (value) => (value ? value : 'N/A'),
              },
            ]}
            tableData={walletDepositRequests}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </>
      )}
    </>
  );
};

export default withAppHOC(MyFundRequestsTable);
